import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import {
    Button,
    Col,
    Collapse,
    CollapseProps,
    DatePicker,
    Form,
    InputNumber,
    Row,
    Select,
    Typography
} from 'antd';
import { useState } from 'react';
import { handlePositiveNumberInputKeyDown } from '@/utils/common';

type ComboFoodSliderSearchProps = {
    onSearch: (values: FilterComboFood) => void;
    listAge: AgeComboFood[];
    listGender: GenderComboFood[];
    listPhysicalActivityComboFood: PhysicalActivityComboFood[],
    listNutritionHealthStatusComboFood: NutritionHealthStatusComboFood[],
    listNutritionStatusComboFood: NutritionStatusComboFood[],
    listNutritionStatusZScoreComboFood: NutritionStatusComboFood[],
    listNutritionStatus60to19ComboFood: NutritionStatusComboFood[]
}
export const ComboFoodManagementSearcher = ({
    onSearch,
    listAge,
    listGender,
    listPhysicalActivityComboFood,
    listNutritionHealthStatusComboFood,
    listNutritionStatusComboFood,
    listNutritionStatusZScoreComboFood,
    listNutritionStatus60to19ComboFood
}: ComboFoodSliderSearchProps) => {
    const [form] = Form.useForm();
    const [disableZSCore, setDisableZSCore] = useState<boolean>(true);
    const [disableBMIDT60_19, setDisableBMIDT60_19] = useState<boolean>(true);
    const [disableBMIDT19, setDisableBMIDT19] = useState<boolean>(true);

    const listAgeOptions = listAge.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listGenderOptions = listGender.map((item) => ({
        value: item.id,
        label: item.name
    }));

    const listPhysicalActivityComboFoodOptions = listPhysicalActivityComboFood.map((item) => ({
        value: item.id,
        label: item.name
    }));

    const listNutritionHealthStatusOptions = listNutritionHealthStatusComboFood.map((item) => ({
        value: item.healthStatusId,
        label: item.displayName
    }));

    const listNutritionStatusOptions = listNutritionStatusComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listNutritionStatusZScoreOptions = listNutritionStatusZScoreComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listNutritionStatus60to19Options = listNutritionStatus60to19ComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const handleSearch = () => {
        const values = form.getFieldsValue();
        onSearch(values);
    }
    const handleClear = () => {
        form.resetFields();
        onSearch({});
    };

    const chooseAge = (option: string) => {
        if (option == 'DT00'
            || option == 'DT00_DT06'
            || option == 'DT01'
            || option == 'DT02'
            || option == 'DT03'
            || option == 'DT03_DT04'
            || option == 'DT04'
            || option == 'DT05'
            || option == 'DT06'
        ) {
            setDisableZSCore(false);
        } else {
            form.setFieldsValue({ ZSCORE_60: undefined });
            setDisableZSCore(true);
        }
        if (option == 'DT07'
            || option == 'DT07_DT12'
            || option == 'DT08'
            || option == 'DT08_DT10'
            || option == 'DT09'
            || option == 'DT10'
            || option == 'DT11'
            || option == 'DT12'
            || option == 'DT12_DT15'
        ) {
            setDisableBMIDT60_19(false);
        } else {
            form.setFieldsValue({ BMI_60_MONTH_19_AGE: undefined });
            setDisableBMIDT60_19(true);
        }
        if (option == 'DT13'
            || option == 'DT13_DT14'
            || option == 'DT14'
            || option == 'DT15'
            || option == 'DT16'
            || option == 'DT17'
            || option == 'DT18'
            || option == 'DT19'
            || option == 'DT20'
        ) {
            setDisableBMIDT19(false);
        } else {
            form.setFieldsValue({ BMI_GE_19_AGE: undefined });
            setDisableBMIDT19(true);
        }
    };

    const items: CollapseProps['items'] = [
        {
            key: '1',
            label: (
                <Typography.Title css={titleStyles} level={5}>
                    BỘ LỌC
                </Typography.Title>
            ),
            children: (
                <Form form={form} css={formStyles}>
                    <Row gutter={24} align='stretch'
                        style={{
                            marginTop: '1rem'
                        }}>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label='Độ tuổi'
                                name='AGE'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                            >
                                <Select
                                    allowClear
                                    options={listAgeOptions}
                                    onSelect={(option) => chooseAge(option)}
                                    onClear={() => {
                                        setDisableZSCore(true);
                                        setDisableBMIDT60_19(true);
                                        setDisableBMIDT19(true);
                                        form.setFieldsValue({
                                            ZSCORE_60: undefined,
                                            BMI_60_MONTH_19_AGE: undefined,
                                            BMI_GE_19_AGE: undefined
                                        });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label='Giới tính'
                                name='GENDER_ID'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                            >
                                <Select
                                    allowClear
                                    options={listGenderOptions}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label='Hoạt động thể lực'
                                name='PHYSICAL_ACTIVITY_LEVEL'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                            >
                                <Select
                                    mode='multiple'
                                    allowClear
                                    options={listPhysicalActivityComboFoodOptions}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24} align='stretch'
                        style={{
                            marginTop: '1rem'
                        }}>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label='Tình trạng sức khỏe'
                                name='HEALTH_STATUS_IDS'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                            >
                                <Select
                                    mode='multiple'
                                    allowClear
                                    options={listNutritionHealthStatusOptions}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label='Cân nặng'
                                css={customLabel}
                                labelCol={{ span: 8 }}>
                                <Row gutter={8} align="middle">
                                    <Col span={11}>
                                        <Form.Item
                                            name="WEIGHT_MIN"
                                            style={{ marginBottom: 0 }}
                                            rules={[
                                                {
                                                    type: 'number',
                                                    min: 0
                                                },
                                            ]}
                                        >
                                            <InputNumber
                                                type='number'
                                                min={0}
                                                style={{ width: '100%' }}
                                                onKeyDown={handlePositiveNumberInputKeyDown}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} style={{ textAlign: "center" }}>-</Col>
                                    <Col span={11}>
                                        <Form.Item
                                            name="WEIGHT_MAX"
                                            style={{ marginBottom: 0 }}
                                            rules={[
                                                {
                                                    type: 'number',
                                                    min: 0
                                                },
                                            ]}
                                        >
                                            <InputNumber
                                                type='number'
                                                min={0}
                                                style={{ width: '100%' }}
                                                onKeyDown={handlePositiveNumberInputKeyDown}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form.Item>
                        </Col>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label="Thời gian cập nhật"
                                style={{ width: "100%" }}
                                css={customLabel}
                                labelCol={{ span: 8 }}
                                colon={false}
                            >
                                <Row gutter={8} align="middle" css={datePickerRowStyle}>
                                    <Col flex={1}>
                                        <Form.Item name="MODIFIED_DATE_START" noStyle>
                                            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                                        </Form.Item>
                                    </Col>
                                    <Col>-</Col>
                                    <Col flex={1}>
                                        <Form.Item name="MODIFIED_DATE_END" noStyle>
                                            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24} align='stretch'
                        style={{
                            marginTop: '0.5rem'
                        }}>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label={<span
                                    css={css`white-space: normal;`}
                                >
                                    Tình trạng dinh dưỡng theo Zscore<br />(&lt;60 tháng tuổi)
                                </span>}
                                name='ZSCORE_60'
                                css={customLabel}
                            >
                                <Select
                                    allowClear
                                    options={listNutritionStatusZScoreOptions}
                                    disabled={disableZSCore}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label={<span
                                    css={css`white-space: normal;`}
                                >
                                    Tình trạng dinh dưỡng theo BMI<br />(60 tháng - 19 tuổi)
                                </span>}
                                name='BMI_60_MONTH_19_AGE'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                            >
                                <Select
                                    allowClear
                                    options={listNutritionStatus60to19Options}
                                    disabled={disableBMIDT60_19}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} css={filterItem}>
                            <Form.Item
                                label={<span
                                    css={css`white-space: normal;`}
                                >
                                    Tình trạng dinh dưỡng theo BMI<br />(&gt;19 tuổi)
                                </span>}
                                name='BMI_GE_19_AGE'
                                css={customLabel}
                            >
                                <Select
                                    allowClear
                                    options={listNutritionStatusOptions}
                                    disabled={disableBMIDT19}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row
                        gutter={16}
                        align='middle'
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '1rem'
                        }}
                    >
                        <Button
                            type='default'
                            icon={<ReloadOutlined />}
                            css={resetButton}
                            onClick={handleClear}
                        >
                            Đặt lại
                        </Button>
                        <Button
                            type='primary'
                            icon={<SearchOutlined />}
                            css={searchButton}
                            onClick={handleSearch}
                        >
                            Tìm kiếm
                        </Button>
                    </Row>
                </Form>
            )
        }
    ];
    return (
        <Row css={boxStyles} justify='space-between' align='middle'>
            <Collapse
                items={items}
                defaultActiveKey={'1'}
                expandIconPosition={'end'}
                css={collapseStyles}
            />
        </Row>
    );
};

const boxStyles = css`
  padding: 0 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const collapseStyles = css`
  width: 100%;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  background-color: transparent !important;

  .ant-collapse-header {
    width: fit-content;
    border-radius: 0.8rem !important;
    border: none !important;
    padding: 0 !important;

    .ant-collapse-expand-icon {
      align-self: center;
    }
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border: none !important;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
  font-weight: 700 !important;
`;

const formStyles = css`
  margin-top: 0.5rem;
  padding: 1rem;
`;

const filterItem = css`
  display: flex;
  justify-content: start !important;
  gap: 0.5rem;

  label {
    height: 100% !important;
  }

  .ant-form-item-label {
    width: fit-content;
    max-width: 33.33%;
    height: 100% !important;
    text-align: start !important;
    white-space: normal;
    font-size: 14px;
    font-weight: 500;
    word-wrap: break-word;
    word-break: break-word;
  }

  input,
  .ant-select,
  .ant-picker {
    flex: 1;
    min-width: 6rem;
  }

  .ant-form-item-control {
    display: flex;
    justify-content: center;
  }

  .ant-form-item-row {
    align-items: center;
  }
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;

    &::after,
    &::before {
      display: none !important;
    }

    margin: 0 2rem 0 0 !important;
  }

  width: 100%;
  margin-bottom: 0;
`;

const resetButton = css`
  margin-right: 10px;
  color: #007bff;
  border-color: #007bff;
`;

const searchButton = css`
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
`;

const datePickerRowStyle = css`
  flex-wrap: wrap;
  
  .ant-col {
    margin-bottom: 0.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    @media (max-width: 576px) {
      flex: 0 0 100% !important;
      max-width: 100% !important;
    }
  }
`;
